/**
 * Strategy Comparison Page
 * Side-by-side comparison of up to 3 strategies
 */
import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  getStrategies,
  formatPrice,
  getRatingStars,
  type MarketplaceStrategy,
} from '../services/marketplaceApi'
import LoadingSpinner from '../components/LoadingSpinner'

// ============================================================================
// Components
// ============================================================================

const StarRating = ({ rating, size = 14 }: { rating: number; size?: number }) => {
  const { full, half, empty } = getRatingStars(rating)
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(full)].map((_, i) => (
        <svg key={`full-${i}`} width={size} height={size} viewBox="0 0 20 20" fill="#f59e0b">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      {[...Array(empty)].map((_, i) => (
        <svg key={`empty-${i}`} width={size} height={size} viewBox="0 0 20 20" fill="#e5e7eb">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  )
}

const MetricRow = ({ 
  label, 
  values, 
  bestId,
  format = 'number',
  lowerIsBetter = false
}: { 
  label: string
  values: Record<string, number>
  bestId?: string
  format?: 'percent' | 'currency' | 'number' | 'rating'
  lowerIsBetter?: boolean
}) => {
  const formatValue = (val: number) => {
    if (format === 'percent') return `${val >= 0 ? '+' : ''}${val.toFixed(1)}%`
    if (format === 'currency') return `₹${val.toFixed(0)}`
    if (format === 'rating') return val.toFixed(1)
    return val.toFixed(2)
  }

  const getValueColor = (strategyId: string) => {
    if (!bestId) return 'text-white'
    if (strategyId === bestId) {
      return lowerIsBetter ? 'text-green-400' : 'text-green-400'
    }
    return 'text-gray-400'
  }

  return (
    <tr className="border-b border-gray-700/50">
      <td className="py-3 pr-4 text-gray-400 text-sm">{label}</td>
      {Object.entries(values).map(([id, value]) => (
        <td 
          key={id} 
          className={`py-3 px-4 text-center font-medium ${getValueColor(id)} ${id === bestId ? 'bg-green-500/10' : ''}`}
        >
          {formatValue(value)}
          {id === bestId && <span className="ml-1 text-xs">🏆</span>}
        </td>
      ))}
    </tr>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export default function Compare() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [strategies, setStrategies] = useState<MarketplaceStrategy[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Get strategy IDs from URL params
  const strategyIds = searchParams.get('ids')?.split(',').filter(Boolean) || []

  useEffect(() => {
    const fetchStrategies = async () => {
      if (strategyIds.length === 0) {
        setError('No strategies selected for comparison')
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        // Fetch all strategies to get details
        const response = await getStrategies()
        if (response.success && response.data) {
          const allStrategies = (response.data as any).strategies || []
          const selected = allStrategies.filter((s: MarketplaceStrategy) => 
            strategyIds.includes(s.strategy_id)
          )
          
          if (selected.length < 2) {
            setError('Not enough strategies found for comparison')
          }
          setStrategies(selected)
        } else {
          setError('Failed to fetch strategies')
        }
      } catch (err) {
        setError('Failed to load comparison data')
      } finally {
        setLoading(false)
      }
    }

    fetchStrategies()
  }, [strategyIds.join(',')])

  // Calculate best performers
  const getBestPerformer = (metric: string, lowerIsBetter = false) => {
    if (strategies.length === 0) return null
    
    let best = strategies[0]
    for (const strategy of strategies) {
      const currentVal = strategy.metrics[metric as keyof typeof strategy.metrics] as number
      const bestVal = best.metrics[metric as keyof typeof best.metrics] as number
      
      if (lowerIsBetter ? currentVal < bestVal : currentVal > bestVal) {
        best = strategy
      }
    }
    return best.strategy_id
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (error || strategies.length < 2) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📊</div>
          <h2 className="text-xl font-bold text-white mb-2">
            {error || 'Select at least 2 strategies to compare'}
          </h2>
          <button
            onClick={() => navigate('/marketplace')}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            Go to Marketplace
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <span>⚖️</span>
                Strategy Comparison
              </h1>
              <p className="text-gray-400 mt-1">
                Comparing {strategies.length} strategies
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/marketplace')}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Back to Marketplace
              </button>
              <button
                onClick={() => {
                  // Clear comparison and go back
                  navigate('/marketplace')
                }}
                className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors"
              >
                New Comparison
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Strategy Headers */}
        <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: `200px repeat(${strategies.length}, 1fr)` }}>
          <div></div>
          {strategies.map(strategy => (
            <div key={strategy.strategy_id} className="bg-gray-800 rounded-xl p-4 text-center">
              <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center text-2xl font-bold text-white">
                {strategy.name.charAt(0).toUpperCase()}
              </div>
              <h3 className="font-semibold text-white line-clamp-1">{strategy.name}</h3>
              <p className="text-sm text-primary mt-1">by {strategy.creator_name}</p>
              <div className="mt-2 flex justify-center">
                <StarRating rating={strategy.avg_rating} />
              </div>
              <div className="mt-2">
                <span className="text-2xl font-bold text-white">
                  {formatPrice(strategy.price, strategy.pricing)}
                </span>
              </div>
              <span className="inline-block mt-2 px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-full">
                {strategy.category.replace('_', ' ')}
              </span>
            </div>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">📊 Performance Metrics</h2>
          </div>
          
          <div className="p-4">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-400 text-sm">
                  <th className="pb-3 pr-4">Metric</th>
                  {strategies.map(s => (
                    <th key={s.strategy_id} className="pb-3 px-4 text-center">Value</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <MetricRow 
                  label="Total Return" 
                  values={Object.fromEntries(strategies.map(s => [s.strategy_id, s.metrics.total_return]))}
                  bestId={getBestPerformer('total_return')}
                  format="percent"
                />
                <MetricRow 
                  label="Win Rate" 
                  values={Object.fromEntries(strategies.map(s => [s.strategy_id, s.metrics.win_rate]))}
                  bestId={getBestPerformer('win_rate')}
                  format="percent"
                />
                <MetricRow 
                  label="Sharpe Ratio" 
                  values={Object.fromEntries(strategies.map(s => [s.strategy_id, s.metrics.sharpe_ratio]))}
                  bestId={getBestPerformer('sharpe_ratio')}
                />
                <MetricRow 
                  label="Max Drawdown" 
                  values={Object.fromEntries(strategies.map(s => [s.strategy_id, s.metrics.max_drawdown]))}
                  bestId={getBestPerformer('max_drawdown', true)}
                  format="percent"
                  lowerIsBetter
                />
                <MetricRow 
                  label="Profit Factor" 
                  values={Object.fromEntries(strategies.map(s => [s.strategy_id, s.metrics.profit_factor]))}
                  bestId={getBestPerformer('profit_factor')}
                />
                <MetricRow 
                  label="Total Trades" 
                  values={Object.fromEntries(strategies.map(s => [s.strategy_id, s.metrics.total_trades]))}
                  bestId={getBestPerformer('total_trades')}
                />
                <MetricRow 
                  label="Avg Trade" 
                  values={Object.fromEntries(strategies.map(s => [s.strategy_id, s.metrics.avg_trade]))}
                  bestId={getBestPerformer('avg_trade')}
                  format="currency"
                />
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t border-gray-700">
            <h2 className="text-lg font-semibold text-white mb-4">⭐ Community Metrics</h2>
            <table className="w-full">
              <tbody>
                <MetricRow 
                  label="Avg Rating" 
                  values={Object.fromEntries(strategies.map(s => [s.strategy_id, s.avg_rating]))}
                  bestId={getBestPerformer('avg_rating')}
                  format="rating"
                />
                <MetricRow 
                  label="Total Ratings" 
                  values={Object.fromEntries(strategies.map(s => [s.strategy_id, s.total_ratings]))}
                  bestId={getBestPerformer('total_ratings')}
                />
                <MetricRow 
                  label="Clone Count" 
                  values={Object.fromEntries(strategies.map(s => [s.strategy_id, s.clone_count]))}
                  bestId={getBestPerformer('clone_count')}
                />
                <MetricRow 
                  label="View Count" 
                  values={Object.fromEntries(strategies.map(s => [s.strategy_id, s.view_count]))}
                  bestId={getBestPerformer('view_count')}
                />
              </tbody>
            </table>
          </div>
        </div>

        {/* Strategy Details */}
        <div className="mt-6 grid gap-4" style={{ gridTemplateColumns: `repeat(${strategies.length}, 1fr)` }}>
          {strategies.map(strategy => (
            <div key={strategy.strategy_id} className="bg-gray-800 rounded-xl p-4">
              <h3 className="font-semibold text-white mb-3">📝 {strategy.name}</h3>
              <p className="text-gray-400 text-sm line-clamp-3">{strategy.description}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {strategy.indicators.slice(0, 4).map(ind => (
                  <span key={ind} className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-full">
                    {ind}
                  </span>
                ))}
              </div>
              <button
                onClick={() => navigate(`/marketplace?strategy=${strategy.strategy_id}`)}
                className="mt-4 w-full py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors"
              >
                View Details
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
